import React, { useEffect, useState } from "react";
import {
  Typography,
  FormGroup,
  FormControlLabel,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  TextField,
  CircularProgress,
} from "@mui/material";

import SecurityIcon from "@mui/icons-material/Security";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import DeleteIcon from "@mui/icons-material/Delete";

import AccountApi from "../../../api_calls/AccountApi";
import { useAuth } from "../../../context/AuthContext";
import { useWebSocket } from "../../../context/WebSocketContext";
import { showSuccess, showError } from "../../../utils/toastUtils";

// Backend failure codes -> messages shown to the user.
const ACCOUNT_ERROR_MESSAGES = {
  invalid_password: "Incorrect password. Please try again.",
  missing_fields: "Please enter your password.",
  invalid_current_status:
    "Your account can't be deactivated right now. Please contact support.",
};

const accountErrorMessage = (response, fallback) =>
  ACCOUNT_ERROR_MESSAGES[response?.data?.message] || fallback;

export default function SettingsTab({
  sweetRed,
  profile,
  user,
  sendMessage,
  isConnected,
}) {
  const { logout } = useAuth();
  const { registerHandler } = useWebSocket();

  const [hidePhotos, setHidePhotos] = useState(false);
  const [showPhotosToAnonymous, setShowPhotosToAnonymous] = useState(false);
  const [hidePhotosFromCountrymates, setHidePhotosFromCountrymates] = useState(
    false
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // sync from profile
  useEffect(() => {
    if (!profile) return;

    setHidePhotos(
      profile?.photos_hidden === true || profile?.photos_hidden === "yes"
    );
    setShowPhotosToAnonymous(
      profile?.show_photos_to_anonymous === true ||
        profile?.show_photos_to_anonymous === "yes"
    );
    setHidePhotosFromCountrymates(
      profile?.hide_photos_from_countrymates === true ||
        profile?.hide_photos_from_countrymates === "yes"
    );
  }, [profile]);

  // === PRIVACY ===

  // Payload key sent to the backend -> setter for that switch.
  const privacySetters = {
    hide_photos: setHidePhotos,
    show_to_anonymous: setShowPhotosToAnonymous,
    hide_photos_from_countrymates: setHidePhotosFromCountrymates,
  };

  // The backend replies on the "privacy_update" tag with the saved value.
  // Apply it so the switch always reflects what was actually stored, and
  // roll back with an error if the save failed.
  useEffect(() => {
    registerHandler("privacy_update", (data) => {
      const setter = privacySetters[data?.field];

      if (data?.status === "success") {
        if (setter) setter(data?.result?.data?.message === "yes");
        showSuccess("Privacy settings updated.");
      } else {
        if (setter) setter((prev) => !prev);
        showError("Could not update privacy settings. Please try again.");
      }
    });
    return () => registerHandler("privacy_update", null);
  }, [registerHandler]);

  const sendPrivacyUpdate = (field, isChecked, setter) => {
    if (!isConnected) {
      showError("You're offline. Please reconnect and try again.");
      return;
    }

    setter(isChecked);
    sendMessage({
      user_id: user?.user_id,
      tag: "privacy_update",
      payload: { [field]: isChecked ? "yes" : "no" },
    });
  };

  const handleHidePhotosToggle = (event) =>
    sendPrivacyUpdate("hide_photos", event.target.checked, setHidePhotos);

  const handleAnonymousPhotoToggle = (event) =>
    sendPrivacyUpdate(
      "show_to_anonymous",
      event.target.checked,
      setShowPhotosToAnonymous
    );

  const handleHidePhotosFromCountrymatesToggle = (event) =>
    sendPrivacyUpdate(
      "hide_photos_from_countrymates",
      event.target.checked,
      setHidePhotosFromCountrymates
    );

  // === DEACTIVATE / DELETE ===

  const closeDialogs = () => {
    if (submitting) return;
    setOpenDeactivateDialog(false);
    setOpenDialog(false);
    setPassword("");
    setPasswordError("");
  };

  const handleDeactivateConfirm = async () => {
    if (!password) {
      setPasswordError("Please enter your password.");
      return;
    }

    setSubmitting(true);
    const response = await AccountApi.toggleAccountStatus(
      user?.user_id,
      password
    );
    setSubmitting(false);

    if (response?.status === "success") {
      showSuccess(
        "Your account has been deactivated. Log in again any time to reactivate it."
      );
      // Short delay so the toast is visible before the hard redirect.
      setTimeout(logout, 2000);
    } else {
      setPasswordError(
        accountErrorMessage(response, "Could not deactivate your account.")
      );
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!password) {
      setPasswordError("Please enter your password.");
      return;
    }

    setSubmitting(true);
    const response = await AccountApi.deleteAccount(user?.user_id, password);
    setSubmitting(false);

    if (response?.status === "success") {
      showSuccess("Your account has been deleted.");
      setTimeout(logout, 2000);
    } else {
      setPasswordError(
        accountErrorMessage(response, "Could not delete your account.")
      );
    }
  };

  const passwordField = (
    <TextField
      autoFocus
      fullWidth
      margin="dense"
      type="password"
      label="Confirm your password"
      value={password}
      onChange={(e) => {
        setPassword(e.target.value);
        setPasswordError("");
      }}
      error={!!passwordError}
      helperText={passwordError}
      disabled={submitting}
      sx={{ mt: 2 }}
    />
  );

  // === UI ===

  return (
    <>
      <Typography
        gutterBottom
        sx={{
          fontWeight: "bold",
          mt: 3,
          display: "flex",
          alignItems: "center",
          color: sweetRed.main,
        }}
      >
        <SecurityIcon sx={{ mr: 1 }} />
        Privacy
      </Typography>

      <Typography sx={{ mt: 2 }}>
        At our matchmaking platform, we prioritize your privacy.
      </Typography>

      <FormGroup>
        <FormControlLabel
          control={
            <Switch checked={hidePhotos} onChange={handleHidePhotosToggle} />
          }
          label="Hide my photos from all users"
        />

        <FormControlLabel
          control={
            <Switch
              checked={hidePhotosFromCountrymates}
              onChange={handleHidePhotosFromCountrymatesToggle}
            />
          }
          label="Hide my photos from people in my country"
        />

        <FormControlLabel
          control={
            <Switch
              checked={showPhotosToAnonymous}
              disabled={hidePhotos}
              onChange={handleAnonymousPhotoToggle}
            />
          }
          label="Show my photos to anonymous users. This option is valid only if your photos are not hidden"
        />
      </FormGroup>

      <Typography sx={{ mt: 1 }}>
        Note that if you hide your photos you will only see photos of users who
        have allowed their photos to be seen by everyone regardless of their
        photo settings
      </Typography>

      {/* Deactivate */}
      <Typography
        gutterBottom
        sx={{
          fontWeight: "bold",
          mt: 3,
          display: "flex",
          alignItems: "center",
          color: sweetRed.main,
        }}
      >
        <PowerSettingsNewIcon sx={{ mr: 1 }} />
        Deactivate account
      </Typography>

      <Typography sx={{ mt: 1 }}>
        Take the reins of your account! Whether life gets hectic or you simply
        need a break, you have the power to temporarily step away and return at
        your convenience. While deactivated, your profile is hidden from other
        users. Simply log in again to reactivate it.
      </Typography>

      <Button
        variant="outlined"
        color="error"
        startIcon={<PowerSettingsNewIcon />}
        sx={{ mt: 2 }}
        onClick={() => setOpenDeactivateDialog(true)}
      >
        Deactivate Account
      </Button>

      <Dialog open={openDeactivateDialog} onClose={closeDialogs}>
        <DialogTitle>Deactivate Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate your account? You will be logged
            out, and you can reactivate it later by logging back in.
          </DialogContentText>
          {passwordField}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateConfirm}
            color="error"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Typography
        gutterBottom
        sx={{
          fontWeight: "bold",
          mt: 3,
          display: "flex",
          alignItems: "center",
          color: sweetRed.main,
        }}
      >
        <DeleteIcon sx={{ mr: 1 }} />
        Delete Account
      </Typography>

      <Box
        sx={{
          border: "1px dashed red",
          padding: 2,
          borderRadius: 1,
          width: "100%",
          margin: "auto",
        }}
      >
        <Typography variant="body1" sx={{ color: "black", mb: 2 }}>
          Ready to part ways? Click "Delete account" to bid farewell. Remember,
          this action is irreversible.
        </Typography>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Delete Account
        </Button>

        <Dialog open={openDialog} onClose={closeDialogs}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete your account? This action cannot
              be undone.
            </DialogContentText>
            {passwordField}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialogs} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirmed}
              color="error"
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : null}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
